import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type { ReimeExerciseConfig, ReimeRunSettings } from "@calega/content";
import type { ReimeRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";

interface ReimeAnswer {
  itemId: string;
  answer: boolean;
  correct: boolean;
}

interface ReimeState {
  result?: { score: number; maxScore: number };
  runId: string;
  run: ReimeRun;
  index: number;
  phase: Phase;
  endsAt: number | null;
  answers: ReimeAnswer[];
}

export function Reime({ account, config, pageId }: { account: Account; config: ReimeExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const syncFollower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || syncFollower;
  const [mode, setMode] = useState<ReimeRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [exercise, setExercise] = useState<ReimeState | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setClockTick] = useState(0);

  const score = useMemo(
    () => (exercise?.answers ?? []).filter((answer) => answer.correct).length,
    [exercise?.answers],
  );

  function publish(next: ReimeState) {
    realtime.publishExercise({ exerciseType: "reime", pageId, state: next });
  }

  function publishReset() {
    realtime.publishExercise({ exerciseType: "reime", pageId, state: null });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: ReimeState | null } | null;
    if (synced?.exerciseType === "reime" && synced.pageId === pageId && synced.state === null) {
      setExercise(null);
      return;
    }
    if (synced?.exerciseType === "reime" && synced.pageId === pageId && synced.state) setExercise(synced.state);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => setClockTick((value) => value + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const remaining = Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow));
    const timer = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), remaining + 20);
    return () => clearExerciseTimeout(timer);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  async function start(settings: ReimeRunSettings) {
    if (busy || !canControl) return;
    setBusy(true);
    setMessage("Durchlauf wird vorbereitet …");
    try {
      const result = await startExerciseRun<{ runId: string; exercise: ReimeRun }>({ contentPageId: pageId, settings });
      const next: ReimeState = {
        runId: result.runId,
        run: result.exercise,
        index: 0,
        phase: "running",
        endsAt: result.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + result.exercise.settings.durationSeconds * 1_000 : null,
        answers: [],
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function startFromSettings() {
    await start({ mode, count, durationSeconds });
  }

  async function abortCurrentRun() {
    if (!exercise || exercise.phase === "complete") return;
    await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || busy) return;
    setBusy(true);
    try {
      await abortCurrentRun();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setExercise(null);
      setMessage("");
      publishReset();
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy) return;
    const settings = exercise.run.settings;
    setBusy(true);
    try {
      await abortCurrentRun();
    } finally {
      setBusy(false);
    }
    await start(settings);
  }

  async function answer(answer: boolean) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "reime", itemId: item.id, answer });
      const next: ReimeState = {
        ...exercise,
        phase: "feedback",
        answers: [...exercise.answers, { itemId: item.id, answer, correct: answer === item.rhymes }],
      };
      setExercise(next);
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canAnswer) return;
    if (exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow) || exercise.index + 1 >= exercise.run.items.length) {
      await finish();
      return;
    }
    const nextState = { ...exercise, index: exercise.index + 1, phase: "running" as const };
    setExercise(nextState);
    publish(nextState);
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next: ReimeState = { ...exercise, result, phase: "complete" };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  if (syncFollower && !exercise) {
    return <ExerciseWaitingCard className="reime-exercise" />;
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (!exercise) {
    return <section className="reime-exercise">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="reime-settings">
        <fieldset>
          <legend>Durchlauf</legend>
          <div className="blitz-choice-row">
            <label className="checkbox-label"><input type="radio" name={`${pageId}-reime-modus`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label>
            <label className="checkbox-label"><input type="radio" name={`${pageId}-reime-modus`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label>
          </div>
        </fieldset>
        {mode === "count" ? <label>Aufgabenanzahl<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((value) => <option value={value} key={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}
      </div>
      <button className="primary" disabled={busy || !canControl} onClick={() => startFromSettings().catch((error) => setMessage(error.message))}>Reimaufgaben starten</button>
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const item = exercise.run.items[exercise.index];
  const lastAnswer = exercise.answers.at(-1);
  const timeRemaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const completed = exercise.answers.length;

  return <section className="reime-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${completed} beantwortet · ${timeRemaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <div className="reime-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? completed} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div> : item && <>
      <div className="reime-card">
        <div className="reime-pair learning-text"><span>{item.first}</span><span>{item.second}</span></div>
        <h2 className="learning-text">Reimt sich das?</h2>
      </div>
      {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback learning-text ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig." : "Nicht ganz."} {item.rhymes ? "Diese Wörter reimen sich." : "Diese Wörter reimen sich nicht."}</div>}
      {canAnswer && <div className="reime-actions">
        {exercise.phase === "running" ? <>
          <button className="secondary reime-yes" disabled={busy} onClick={() => answer(true).catch((error) => setMessage(error.message))}>✓ Ja</button>
          <button className="secondary reime-no" disabled={busy} onClick={() => answer(false).catch((error) => setMessage(error.message))}>✕ Nein</button>
        </> : <button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow) || exercise.index + 1 >= exercise.run.items.length ? "Auswertung ansehen" : "Nächstes Wortpaar"}</button>}
      </div>}
    </>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
